import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from '../services/api';
import '../styles/Login.css';
import AkrosLogo from '../components/AkrosLogo';

const AcceptInvite = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const [inviteUser, setInviteUser] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    const loadInvite = async () => {
      if (!token) {
        setError('Invite token is missing.');
        setLoading(false);
        return;
      }

      try {
        const response = await api.get(`/auth/invite/${encodeURIComponent(token)}`);
        setInviteUser(response.data.user);
      } catch (err) {
        setError(err.response?.data?.message || 'Invite link is invalid or expired.');
      } finally {
        setLoading(false);
      }
    };

    loadInvite();
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      await api.post('/auth/accept-invite', { token, password });
      setSuccess('Your password is set. Redirecting to sign in...');
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      setError(err.response?.data?.message || 'Could not accept invite.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{
      bgcolor: 'var(--color-dark)',
      minHeight: '100vh',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 2
    }}>
      <Paper elevation={3} className="auth-card" sx={{
        width: '100%',
        maxWidth: '480px',
        position: 'relative',
        pt: 0,
        px: 1,
        pb: 3
      }}>
        <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%', mt: 24 }}>
          <Box sx={{
            position: 'absolute',
            top: 8,
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100%',
            mb: 3,
            px: 1
          }}>
            <Box
              sx={{
                width: '100%',
                minHeight: 176,
                bgcolor: 'var(--color-dark)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 4
              }}
            >
              <AkrosLogo
                iconColor="var(--color-primary)"
                nameColor="var(--color-white)"
                iconSize={72}
                height={54}
                gap={18}
                sx={{ width: '100%', justifyContent: 'center' }}
              />
            </Box>
          </Box>

          <Box sx={{ width: '100%', p: 0, px: 4 }}>
            <Typography component="h1" variant="h6" fontWeight="700" sx={{ fontSize: '1.2rem !important', pb: 2 }}>
              Set Up Your Account
            </Typography>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : (
              <Box>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                {inviteUser && !success && (
                  <Box>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                      {inviteUser.firstName} {inviteUser.lastName} · {inviteUser.email}
                    </Typography>

                    <Typography component="label" variant="subtitle2" fontWeight="600">
                      Password*
                    </Typography>
                    <TextField
                      margin="dense"
                      required
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      disabled={submitting}
                      autoComplete="new-password"
                      sx={{ mb: 2 }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        )
                      }}
                    />

                    <Typography component="label" variant="subtitle2" fontWeight="600">
                      Confirm Password*
                    </Typography>
                    <TextField
                      margin="dense"
                      required
                      fullWidth
                      type={showPassword ? 'text' : 'password'}
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      disabled={submitting}
                      autoComplete="new-password"
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                      <Button
                        type="submit"
                        variant="outlined"
                        size="small"
                        className="login-button"
                        disabled={submitting}
                        sx={{ height: '40px', minHeight: '40px' }}
                      >
                        {submitting ? <CircularProgress size={24} /> : 'Set Password'}
                      </Button>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default AcceptInvite;
